import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action, Dispatch } from 'redux';

import { OrderSideEnum } from '../../lib/enums';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getCompanyLogoAndName as getLogoAndName } from '../../lib/store/fundamental';
import { formatCurrency } from '../../lib/util/DataHelpers';
import { TextHelpers } from '../../lib/util/TextHelpers';
import { convertOrderSideToText } from '../../lib/util/TradingHelpers';

import iconClose from '../../assets/img/close-line.svg';
import Company from '../Chart/SymbolChart/ChartHeader/Components/Company';
import Logo from '../Chart/SymbolChart/ChartHeader/Components/Logo';
import ClickableImage from '../ClickableImage/ClickableImage';
import { ClickableDiv } from '..';

import styles from './ConfirmOrderPopupMobile.module.scss';

type OwnProps = {
  symbol: string;
  action: string;
  orderType: string;
  sharescount: number | string;
  marketPrice?: number | string | null;
  targetPrice?: number | string;
  orderValue: number | string;
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
};

type Props = OwnProps & ReduxProps;

const ConfirmOrderPopupMobile = (props: Props) => {
  const {
    symbol,
    action,
    orderType,
    sharescount,
    marketPrice,
    targetPrice,
    orderValue,
    companyLogoAndName,
    onCancel,
    onConfirm,
    getCompanyLogoAndName,
  } = props;

  const orderTypeText = TextHelpers.getOrderTypeName(orderType as string);
  const orderTypeName = action === OrderSideEnum.SellShort
    ? `${orderTypeText} ${lang.commonShortSell3()}`
    : lang.commonTradingOrderTitle(
      orderTypeText,
      convertOrderSideToText(action),
    );

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName();
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  const logo = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.logo : undefined), [companyLogoAndName, symbol]);

  const url = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.url : undefined), [companyLogoAndName, symbol]);

  const symbolName = companyLogoAndName[symbol].name;

  return (
    <ClickableDiv className={styles.popupWrapper}>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          <div className={styles.symbolAndNameWrapper}>
            <div className={styles.symbolAndName}>
              <Logo logo={logo} url={url} symbol={symbol} />
              <Company company={symbolName} symbol={symbol} />
            </div>
            <ClickableImage src={iconClose} onClick={() => onCancel(false)} />
          </div>
          <div className={styles.detailsContainer}>
            <div className={styles.textLine}>
              <div>{lang.commonTradingOrderType()}</div>
              <div>{orderTypeName}</div>
            </div>
            <div className={styles.textLine}>
              <div>{lang.mobileTradingTicketNumberOfShares()}</div>
              <div>{sharescount}</div>
            </div>
            <div className={styles.textLine}>
              <div>{lang.commonTradeHistoryDetailsPrice(orderTypeText)}</div>
              <div>{formatCurrency(orderType !== OrderTypeEnum.Market ? targetPrice : marketPrice)}</div>
            </div>
            <div className={styles.borderLine} />
            <div className={styles.textLine}>
              <div>{lang.commonTradingOrderValue()}</div>
              <div>{formatCurrency(orderValue)}</div>
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <button className={styles.confirmButton} type="button" onClick={onConfirm}>
              {' '}
              {lang.commonConfirmOrderButton2()}
              {' '}
            </button>
            <button
              className={styles.cancelButton}
              type="button"
              onClick={() => {
                onCancel(false);
              }}
            >
              {' '}
              {lang.commonCancelMultiple()}
              {' '}
            </button>
          </div>
        </div>
      </div>
    </ClickableDiv>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps) => ({
  getCompanyLogoAndName: () => dispatch(getLogoAndName(ownProps.symbol)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ConfirmOrderPopupMobile);
